import { Stack, TextField, Typography, Autocomplete } from '@mui/material';
import type { ResourceIdentifiers } from '@repo/api-gw-sdk';
import { forwardRef, type HTMLAttributes, useState } from 'react';
import { Virtuoso } from 'react-virtuoso';

import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
} from '@/components/accordion';
import { OptionMenu } from '@/components/option-menu/option-menu';

import { ResourceIdentifierItem } from './resourceIdentifierItem';

import { AddButton } from '../buttons/addButton';

const ITEM_HEIGHT = 56;
const MAX_LISTBOX_HEIGHT = 300;

// Virtualized listbox for the Autocomplete dropdown.
const VirtualizedListbox = forwardRef<
  HTMLDivElement,
  HTMLAttributes<HTMLElement>
>(function VirtualizedListbox({ children, ...props }, ref) {
  const items = Array.isArray(children) ? children.flat() : [];
  const height = Math.min(items.length * ITEM_HEIGHT, MAX_LISTBOX_HEIGHT);
  return (
    <div ref={ref} {...props}>
      <Virtuoso
        style={{ height }}
        totalCount={items.length}
        itemContent={(index) => items[index]}
      />
    </div>
  );
});

interface Props {
  label: string;
  description: string;
  allResources: ResourceIdentifiers[];
  filteredResources?: ResourceIdentifiers[];
  resources: string[];
  initialResourcesIds?: string[];
  addResource: (providerResourceId: string) => void;
  removeResource: (providerResourceId: string) => void;
  disabled?: boolean;
  disabledDeletion?: boolean;
  freeTextAdd?: boolean;
  onFilterChange?: (text: string) => void;
}

export const ResourcesSelectorAccordion = (props: Props) => {
  const [isExpanded, setIsExpanded] = useState(false);

  return (
    <Accordion expanded={isExpanded}>
      <AccordionSummary onClick={() => setIsExpanded(!isExpanded)}>
        <Stack
          width='100%'
          direction='row'
          alignItems='center'
          justifyContent='space-between'
        >
          <Typography variant='body1'>{props.label}</Typography>
          {!isExpanded && (
            <Typography
              sx={{ fontWeight: '400 !important' }}
              variant='subtitle1'
            >
              {props.resources.length}
            </Typography>
          )}
        </Stack>
      </AccordionSummary>
      <AccordionDetails sx={{ padding: '12px 0px 24px 36px' }}>
        <ResourcesSelector {...props} onFilterChange={props.onFilterChange} />
      </AccordionDetails>
    </Accordion>
  );
};

export const ResourcesSelector = ({
  description,
  allResources,
  filteredResources,
  resources,
  addResource,
  removeResource,
  disabled,
  disabledDeletion,
  freeTextAdd = true,
  initialResourcesIds,
  onFilterChange,
}: Props) => {
  const [filter, setFilter] = useState('');
  const [highlighted, setHighlighted] = useState<ResourceIdentifiers | null>(
    null
  );

  const dropdownResources = filteredResources ?? allResources;

  const onAddPressed = (id: string) => {
    if (!resources.includes(id) && id.length > 0) {
      addResource(id);
      setFilter('');
      onFilterChange?.('');
      setHighlighted(null);
    }
  };

  const options = dropdownResources
    .filter((resource) => !resources.includes(resource.providerId))
    .filter(
      (resource, index, self) =>
        self.findIndex((r) => r.providerId === resource.providerId) === index
    );

  return (
    <Stack marginY='12px 24px' gap='24px'>
      <Stack
        direction='row'
        gap='12px'
        alignItems='center'
        justifyContent='space-between'
      >
        <Autocomplete
          disabled={disabled}
          sx={{
            flexGrow: 1,
            '& .MuiAutocomplete-endAdornment': {
              display: 'none',
            },
          }}
          clearOnBlur={false}
          options={options}
          // prevents MUI from trying to hold onto a selected value that might have been filtered out of options
          value={null}
          filterOptions={(x) => x}
          slotProps={{ listbox: { component: VirtualizedListbox } }}
          getOptionLabel={(option) =>
            typeof option === 'string' ? option : option.providerId
          }
          renderOption={(props, option) => (
            <li {...props} key={option.providerId}>
              <ResourceIdentifierItem resource={option} />
            </li>
          )}
          noOptionsText={
            freeTextAdd
              ? 'Resource not discovered yet. You can add it now to include it once available.'
              : 'No resources found.'
          }
          inputValue={filter}
          onInputChange={(event, newInputValue) => {
            // for when reset the filter
            if (event) {
              setFilter(newInputValue);
              onFilterChange?.(newInputValue);
            }
          }}
          onHighlightChange={(event, option) => {
            setHighlighted(option);
          }}
          onChange={(event, value, reason) => {
            if (reason === 'selectOption' && value) {
              onAddPressed(value.providerId);
            }
          }}
          renderInput={(params) => (
            <TextField
              {...params}
              sx={{
                '& .MuiInputBase-root': {
                  height: '32px',
                },
                '& .MuiInputBase-input': {
                  padding: 0,
                  height: 'auto',
                },
              }}
              placeholder='Search or type resource ID...'
              slotProps={{ input: { ...params.InputProps } }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  if (highlighted) {
                    // Commit the highlighted option ourselves to avoid cases where onChange doesn't fire.
                    e.preventDefault();
                    e.stopPropagation();
                    onAddPressed(highlighted.providerId);
                    return;
                  }

                  if (freeTextAdd) {
                    onAddPressed(filter);
                  }
                }
              }}
            />
          )}
        />
        {freeTextAdd && (
          <AddButton
            disabled={!filter || !!disabled}
            onClick={() => onAddPressed(filter)}
          />
        )}
      </Stack>
      {description && (
        <Typography variant='subtitle1'>{description}</Typography>
      )}
      <Stack gap='12px'>
        {resources
          .map(
            (r) =>
              allResources.find((resource) => resource.providerId === r) || {
                id: '',
                providerId: r,
                name: 'Not discovered yet',
              }
          )
          .filter((resource) => resource !== undefined)
          .map((resource) => {
            return (
              <Stack
                key={resource.providerId}
                direction='row'
                gap='12px'
                alignItems='center'
                justifyContent='space-between'
              >
                <ResourceIdentifierItem resource={resource} />
                <OptionMenu
                  iconClassName='text-(--mui-palette-text-primary)'
                  options={[
                    {
                      text: 'Remove',
                      menuItemProps: {
                        disabled:
                          disabledDeletion &&
                          initialResourcesIds?.includes(resource.providerId),
                        onClick: () => removeResource(resource.providerId),
                      },
                      id: 'remove-resource',
                    },
                  ]}
                />
              </Stack>
            );
          })}
      </Stack>
    </Stack>
  );
};
